import { FC, useEffect, useState } from 'react';
import { useFormik } from 'formik';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import TextField from 'src/components/InputFields/TextField';
import Header from 'src/components/Headers/Header';
import ExistingError from 'src/components/Errors/ErrorAlert';
import { loginValidationSchema } from 'src/utils/validationScheme.utils';
import { errorMessages, routeURL } from 'src/utils/properties.utils';
import { loginInitialValues } from 'src/utils/initialValues.utils';
import { handleLoginDataStorage } from 'src/utils/token.utils';
import { useNavigate } from 'react-router-dom';
import { login } from 'src/services/auth.service';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  AuthFormContainer,
  AuthTitle,
  AuthFieldsContainer,
  AuthButton,
  VerificationPageContainer,
  AuthFooterContainer,
  AuthFooterText,
  AuthFooterLink,
} from 'src/components/StyledComponents/StyledAuthPages';
import { useUserProfile } from 'src/context/UserProfileContext';
import { ButtonStyled } from 'src/components/StyledComponents/StyledPageComponents';

const Login: FC = () => {
  const [errorMessage] = useState<string>('');
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const mdScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { showSnackbar } = StyledSnackbar();
  const { refreshUserProfile } = useUserProfile();

  useEffect(() => {
    const sessionExpired = sessionStorage.getItem('sessionExpired');
    if (sessionExpired) {
      showSnackbar(errorMessages.SESSION_EXPIRED, 'warning');
      sessionStorage.removeItem('sessionExpired');
    }
  }, []);

  const handleLogin = async (values: any) => {
    try {
      const response = await login(values);
      const { tokens, dashboardUrl, message, fullName, roles } = response.data;
      handleLoginDataStorage(tokens, roles, fullName);

      await refreshUserProfile();

      navigate(routeURL.USER + routeURL.DASHBOARD);
      showSnackbar(message, 'success');
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    }
  };

  const formik = useFormik({
    initialValues: loginInitialValues,
    validationSchema: loginValidationSchema,
    onSubmit: (values) => handleLogin(values),
  });

  return (
    <VerificationPageContainer xsPadding={1} smPadding={1.5}>
      <Header smScreen={smScreen} />

      {/* Refactored inline sx from lines 76-83 to AuthFormContainer styled component */}
      <AuthFormContainer
        smScreen={smScreen}
        mdScreen={mdScreen}
        component="form"
        onSubmit={formik.handleSubmit as any}
      >
        <AuthTitle smScreen={smScreen} mdScreen={mdScreen}>
          Welcome Back!
        </AuthTitle>

        {errorMessage && (
          <ExistingError message={errorMessage} severity="error" />
        )}

        <AuthFieldsContainer smScreen={smScreen}>
          <TextField
            id="email"
            label="Email"
            type="email"
            formikProps={formik}
          />
          <TextField
            id="password"
            label="Password"
            type="password"
            showPasswordToggle
            formikProps={formik}
          />
        </AuthFieldsContainer>

        <AuthButton smScreen={smScreen}>
          <ButtonStyled fullWidth type="submit">
            Sign In
          </ButtonStyled>
        </AuthButton>

        <AuthFooterContainer smScreen={smScreen} mdScreen={mdScreen}>
          <AuthFooterText smScreen={smScreen}>
            Don't have an account?{' '}
            <AuthFooterLink href="/signup" smScreen={smScreen}>
              Sign Up
            </AuthFooterLink>
          </AuthFooterText>
          <AuthFooterText smScreen={smScreen}>
            <AuthFooterLink href={routeURL.FORGOT_PASSWORD} smScreen={smScreen}>
              Forgot Password?
            </AuthFooterLink>
          </AuthFooterText>
        </AuthFooterContainer>
      </AuthFormContainer>
    </VerificationPageContainer>
  );
};

export default Login;
